import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import DataTable from "react-data-table-component";
import {
  Add,
  ViewGeneralSettingTitle,
  GeneralSettingTitle,
} from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, P } from "../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import useCustomToasts from "../../../hooks/useCustomToast";
import {
  Eye,
  EyeOff,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Video,
  Lock,
  FileText,
  Shield,
  Info,
  Copy,
  Check,
  ExternalLink,
} from "react-feather";
import { useAuth } from "../../../Context/AuthContext";

interface ExtendedGeneralSetting {
  id: string;
  phone: string;
  address: string;
  email: string;
  about: string;
  logo: string;
  currency: string;
  term_condition: string;
  customer_term_condition?: string;
  is_video?: number | string;
  is_target?: boolean | number | string;
  target_limit?: number | string | null;
  is_active?: number;
  is_deleted?: number;
  created_at?: string;
  updated_at?: string;
}

export default function ViewGeneralSetting() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<ExtendedGeneralSetting[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  const { hasPermission } = useAuth();

  // Detailed Modal View states (Exact 1:1 match with Agent View design theme)
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState<ExtendedGeneralSetting | null>(null);
  const [modalActiveTab, setModalActiveTab] = useState("1");
  const [showPassword, setShowPassword] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}general_setting/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data: ExtendedGeneralSetting[] = response.data;
        setFilteredItems(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refresh]);

  const handleAdd = () => {
    navigate("/add_general_setting");
  };

  const filteredData = filteredItems.filter((item: ExtendedGeneralSetting) => {
    const search = filterText.toLowerCase();
    return (
      (item.phone || "").toLowerCase().includes(search) ||
      (item.address || "").toLowerCase().includes(search) ||
      (item.email || "").toLowerCase().includes(search) ||
      (item.about || "").toLowerCase().includes(search) ||
      (item.term_condition || "").toLowerCase().includes(search) ||
      (item.logo || "").toLowerCase().includes(search)
    );
  });

  const handleEdit = (id: string) => {
    navigate("/edit_general_setting", { state: { id: id } });
  };

  const handleViewDetail = async (row: ExtendedGeneralSetting) => {
    setSelectedSetting(row);
    setModalActiveTab("1");
    setShowPassword(false);
    setDetailModalOpen(true);
    setLoadingDetail(true);

    try {
      const response = await axios.post(
        `${baseUrl}general_setting/viewbyid`,
        { id: row.id },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data) {
        setSelectedSetting(response.data);
      }
    } catch (err) {
      console.error("Error fetching general setting detail by id:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this generalSetting?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}general_setting/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`General Setting Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setFilteredItems((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );
      } catch (error) {
        console.error("Error deleting general setting:", error);
        toast.error("An error occurred while deleting the general setting", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  // Table Columns Definition
  const generalSettingTableColumns = [
    {
      name: <div>Logo</div>,
      cell: (row: ExtendedGeneralSetting) => (
        <img
          src={`${mediaUrl}/${row.logo}`}
          alt={row.logo}
          width={32}
          height={32}
          style={{
            objectFit: "cover",
            borderRadius: "50%",
            border: "1px solid #dee2e6",
          }}
        />
      ),
      width: "70px",
    },
    {
      name: <div>Contact Number</div>,
      selector: (row: ExtendedGeneralSetting) => row.phone,
      sortable: true,
      width: "160px",
    },
    {
      name: <div>Address</div>,
      selector: (row: ExtendedGeneralSetting) => row.address,
      sortable: true,
      minWidth: "160px",
      wrap: true,
    },
    {
      name: <div>Email</div>,
      selector: (row: ExtendedGeneralSetting) => row.email,
      sortable: true,
      minWidth: "180px",
      wrap: true,
    },
    {
      name: <div>About</div>,
      selector: (row: ExtendedGeneralSetting) => row.about,
      sortable: true,
      minWidth: "200px",
      wrap: true,
    },
    {
      name: <div>Currency</div>,
      selector: (row: ExtendedGeneralSetting) => row.currency,
      sortable: true,
      width: "110px",
    },
    {
      name: <div>Actions</div>,
      cell: (row: ExtendedGeneralSetting) => (
        <div className="d-flex align-items-center justify-content-center gap-1">
          {hasPermission("general-settings.view") && (
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewDetail(row)}
              style={{
                padding: "4px 8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="View Details"
            >
              <Eye size={13} />
            </Button>
          )}
          {hasPermission("general-settings.edit") && (
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEdit(row.id)}
              style={{
                padding: "4px 8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Edit"
            >
              <i className="icon-pencil-alt" style={{ fontSize: "13px" }} />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      center: true,
      width: "150px",
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  const customStyles = {
    headCells: {
      style: {
        fontSize: "15px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  if (!hasPermission("general-settings.view")) {
    return (
      <div className="page-body">
        <Container fluid>
          <Row className="justify-content-center align-items-center" style={{ minHeight: "calc(100vh - 160px)" }}>
            <Col md={7} lg={5} className="text-center">
              <Card
                className="border-0 shadow-lg position-relative overflow-hidden"
                style={{
                  borderRadius: "24px",
                  boxShadow: "0 20px 50px -10px rgba(0, 0, 0, 0.08)",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    height: "5px",
                    width: "100%",
                    background: "linear-gradient(90deg, #308e87 0%, #00b4d8 100%)",
                  }}
                />
                <CardBody className="p-4 p-md-5">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: "84px",
                      height: "84px",
                      background: "linear-gradient(135deg, rgba(48, 142, 135, 0.1) 0%, rgba(0, 180, 216, 0.15) 100%)",
                      color: "#308e87",
                      border: "1px solid rgba(48, 142, 135, 0.2)",
                    }}
                  >
                    <i className="fa fa-info-circle fa-2x"></i>
                  </div>
                  <div>
                    <span
                      className="badge bg-light-info text-info mb-3 px-3 py-2 rounded-pill fw-bold"
                      style={{ fontSize: "0.75rem", letterSpacing: "0.8px", textTransform: "uppercase" }}
                    >
                      Configuration Active
                    </span>
                  </div>
                  <H3 className="fw-bold text-dark mb-2">Setting Configuration Already Exists</H3>
                  <P className="text-muted mb-0 px-lg-2" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                    {filteredItems.length > 0
                      ? "Only one General Setting configuration is permitted in the system and a record is already active. Creating an additional record is disabled."
                      : "You do not have administrative privileges to view General Settings."}
                  </P>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        pageTitle={ViewGeneralSettingTitle}
        parent={GeneralSettingTitle}
        title={ViewGeneralSettingTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {/* If no record exists, allow adding a new one */}
            {filteredItems.length === 0 && hasPermission("general-settings.add") && (
              <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
                <Btn color="primary" onClick={handleAdd}>
                  {Add}
                </Btn>
              </Col>
            )}

            {/* Informative notice when record already exists and user has ADD permission */}
            {filteredItems.length > 0 && hasPermission("general-settings.add") && (
              <div className="alert alert-light-info d-flex align-items-center mb-3 p-3 border border-info rounded-3" style={{ fontSize: "13px" }}>
                <i className="fa fa-info-circle me-2 text-info fs-5"></i>
                <div>
                  <strong>Configuration Active:</strong> A General Setting record already exists. Only one configuration is permitted in the system, so creating an additional record is disabled.
                </div>
              </div>
            )}
            <div className="table-responsive">
              <DataTable
                data={filteredData}
                columns={generalSettingTableColumns}
                pagination
                highlightOnHover
                striped
                customStyles={customStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>



      {/* General Setting Detail Modal - EXACT MATCH WITH AGENT DETAIL VIEW */}
      <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModalOpen(false)} className="border-bottom-0 pb-0">
          <span className="fw-bold text-dark fs-5">General Setting Details</span>
        </ModalHeader>
        <ModalBody className="px-4 pb-4">
          {selectedSetting && (
            <div>
              {loadingDetail ? (
                <div className="text-center p-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted fw-semibold">Loading setting details...</p>
                </div>
              ) : (
                <>
                  {/* Hero Card */}
                  <div
                    className="text-center mb-4 p-4 rounded-4 position-relative overflow-hidden"
                    style={{
                      background: "linear-gradient(135deg, #1b9aaa 0%, #16827c 100%)",
                      color: "#ffffff",
                      boxShadow: "0 8px 24px rgba(22, 130, 124, 0.25)",
                    }}
                  >
                    <div className="d-inline-block position-relative mb-2">
                      {selectedSetting.logo ? (
                        <img
                          src={`${mediaUrl}/${selectedSetting.logo}`}
                          alt="App Logo"
                          width="90"
                          height="90"
                          style={{
                            objectFit: "cover",
                            borderRadius: "50%",
                            border: "3px solid #ffffff",
                            boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                            backgroundColor: "#ffffff",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center bg-white text-dark rounded-circle mx-auto fw-bold fs-3"
                          style={{ width: "90px", height: "90px", border: "3px solid #ffffff" }}
                        >
                          GS
                        </div>
                      )}
                    </div>
                    <h3 className="fw-bold text-white mb-1">
                      General Settings Overview
                    </h3>
                    <p className="text-white-50 small mb-2">{selectedSetting.email || "System Configuration"}</p>
                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                      <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.25)",
                          backdropFilter: "blur(4px)",
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      >
                        Currency: <strong>{selectedSetting.currency || "N/A"}</strong>
                      </span>
                      <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                          backgroundColor: selectedSetting.is_video == 1 || selectedSetting.is_video == "1" ? "#10b981" : "#6b7280",
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      >
                        ● Video Service: {selectedSetting.is_video == 1 || selectedSetting.is_video == "1" ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>

                  {/* Navigation Tabs */}
                  <Nav tabs className="border-tab mb-4 flex-wrap gap-1">
                    <NavItem>
                      <NavLink
                        className={modalActiveTab === "1" ? "active" : ""}
                        onClick={() => setModalActiveTab("1")}
                        style={{ cursor: "pointer", fontWeight: "600" }}
                      >
                        <i className="fa fa-info-circle me-2 text-info" /> General Info
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={modalActiveTab === "2" ? "active" : ""}
                        onClick={() => setModalActiveTab("2")}
                        style={{ cursor: "pointer", fontWeight: "600" }}
                      >
                        <i className="fa fa-cogs me-2 text-primary" /> System & Mail
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={modalActiveTab === "3" ? "active" : ""}
                        onClick={() => setModalActiveTab("3")}
                        style={{ cursor: "pointer", fontWeight: "600" }}
                      >
                        <i className="fa fa-file-text me-2 text-success" /> Agent T&C
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={modalActiveTab === "4" ? "active" : ""}
                        onClick={() => setModalActiveTab("4")}
                        style={{ cursor: "pointer", fontWeight: "600" }}
                      >
                        <i className="fa fa-shield me-2 text-warning" /> Customer T&C
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={modalActiveTab}>
                    {/* Tab 1: General Information */}
                    <TabPane tabId="1">
                      <h6 className="fw-bold text-dark mb-3 text-uppercase tracking-wider fs-7">
                        <i className="fa fa-user me-2 text-info" /> Primary Contact & Information
                      </h6>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{ width: "42px", height: "42px", backgroundColor: "#e0f2fe", color: "#0284c7" }}
                            >
                              <Phone size={20} />
                            </div>
                            <div>
                              <span className="text-muted small d-block fw-semibold">Phone Number</span>
                              <strong className="text-dark">{selectedSetting.phone || "N/A"}</strong>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{ width: "42px", height: "42px", backgroundColor: "#f0fdf4", color: "#166534" }}
                            >
                              <Mail size={20} />
                            </div>
                            <div>
                              <span className="text-muted small d-block fw-semibold">Email Address</span>
                              <strong className="text-dark">{selectedSetting.email || "N/A"}</strong>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{ width: "42px", height: "42px", backgroundColor: "#fef3c7", color: "#b45309" }}
                            >
                              <DollarSign size={20} />
                            </div>
                            <div>
                              <span className="text-muted small d-block fw-semibold">Currency</span>
                              <strong className="text-dark">{selectedSetting.currency || "N/A"}</strong>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{ width: "42px", height: "42px", backgroundColor: "#fae8ff", color: "#86198f" }}
                            >
                              <Video size={20} />
                            </div>
                            <div>
                              <span className="text-muted small d-block fw-semibold">Video Calling Service</span>
                              <span className={`badge ${selectedSetting.is_video == 1 || selectedSetting.is_video == "1" ? "bg-success" : "bg-secondary"}`}>
                                {selectedSetting.is_video == 1 || selectedSetting.is_video == "1" ? "Enabled" : "Disabled"}
                              </span>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-center">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{ width: "42px", height: "42px", backgroundColor: "#ecfdf5", color: "#047857" }}
                            >
                              <Shield size={20} />
                            </div>
                            <div>
                              <span className="text-muted small d-block fw-semibold">Withdrawal Target Setting</span>
                              <span className={`badge ${selectedSetting.is_target == 1 || selectedSetting.is_target === true || selectedSetting.is_target == "1" ? "bg-success" : "bg-secondary"} me-2`}>
                                {selectedSetting.is_target == 1 || selectedSetting.is_target === true || selectedSetting.is_target == "1" ? "Active" : "Inactive"}
                              </span>
                              {(selectedSetting.is_target == 1 || selectedSetting.is_target === true || selectedSetting.is_target == "1") && selectedSetting.target_limit && (
                                <strong className="text-dark small">Limit: {selectedSetting.target_limit}</strong>
                              )}
                            </div>
                          </div>
                        </Col>

                        <Col md={12}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex align-items-start">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0 mt-1"
                              style={{ width: "42px", height: "42px", backgroundColor: "#fff7ed", color: "#c2410c" }}
                            >
                              <MapPin size={20} />
                            </div>
                            <div>
                              <span className="text-muted small d-block fw-semibold">Physical Address</span>
                              <strong className="text-dark">{selectedSetting.address || "N/A"}</strong>
                            </div>
                          </div>
                        </Col>

                        <Col md={12}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm h-100">
                            <div className="d-flex align-items-center mb-2">
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                style={{ width: "32px", height: "32px", backgroundColor: "#ecfeff", color: "#0891b2" }}
                              >
                                <Info size={16} />
                              </div>
                              <span className="text-muted small fw-semibold">About Company / Platform</span>
                            </div>
                            <div className="p-3 bg-light rounded border text-dark" style={{ lineHeight: "1.6", fontSize: "14px" }}>
                              {selectedSetting.about || "No description provided."}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </TabPane>

                    {/* Tab 2: System & Mail */}
                    <TabPane tabId="2">
                      <h6 className="fw-bold text-dark mb-3 text-uppercase tracking-wider fs-7">
                        <i className="fa fa-cogs me-2 text-primary" /> System & Authentication Config
                      </h6>
                      <Row className="g-3">
                        <Col md={12}>
                          <div className="p-3 border rounded-3 bg-white shadow-sm">
                            <span className="text-muted small d-block fw-semibold mb-3">App Logo Asset</span>
                            <div
                              className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-3 rounded-3"
                              style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}
                            >
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  className="d-flex align-items-center justify-content-center bg-white rounded-3 p-2 shadow-sm border"
                                  style={{ width: "70px", height: "70px" }}
                                >
                                  <img
                                    src={`${mediaUrl}/${selectedSetting.logo}`}
                                    alt="App Logo"
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                  />
                                </div>
                                <div>
                                  <h6 className="fw-bold text-dark mb-1">Application Logo</h6>
                                  <span className="badge bg-light text-secondary border px-2 py-1 small">
                                    Original Branding Media
                                  </span>
                                </div>
                              </div>
                              <a
                                href={`${mediaUrl}/${selectedSetting.logo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center px-3 py-2 fw-semibold rounded-2"
                              >
                                <ExternalLink size={14} className="me-1" /> View Full Image
                              </a>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </TabPane>

                    {/* Tab 3: Agent T&C */}
                    <TabPane tabId="3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider fs-7">
                          <i className="fa fa-file-text me-2 text-success" /> Agent Terms & Conditions
                        </h6>
                        <span className="badge bg-success-light text-success fw-semibold px-2 py-1 border border-success">
                          Active Policy
                        </span>
                      </div>
                      <div
                        className="p-4 bg-white rounded-3 border shadow-sm text-dark"
                        style={{ maxHeight: "400px", overflowY: "auto", fontSize: "14px", lineHeight: "1.6" }}
                      >
                        {selectedSetting.term_condition ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedSetting.term_condition }} />
                        ) : (
                          <div className="text-center p-4 text-muted">
                            <FileText size={32} className="mb-2 text-muted" />
                            <p className="mb-0">No agent terms and conditions have been configured.</p>
                          </div>
                        )}
                      </div>
                    </TabPane>

                    {/* Tab 4: Customer T&C */}
                    <TabPane tabId="4">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider fs-7">
                          <i className="fa fa-shield me-2 text-warning" /> Customer Terms & Conditions
                        </h6>
                        <span className="badge bg-warning-light text-dark fw-semibold px-2 py-1 border border-warning">
                          Active Policy
                        </span>
                      </div>
                      <div
                        className="p-4 bg-white rounded-3 border shadow-sm text-dark"
                        style={{ maxHeight: "400px", overflowY: "auto", fontSize: "14px", lineHeight: "1.6" }}
                      >
                        {selectedSetting.customer_term_condition ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedSetting.customer_term_condition }} />
                        ) : (
                          <div className="text-center p-4 text-muted">
                            <Shield size={32} className="mb-2 text-muted" />
                            <p className="mb-0">No customer terms and conditions have been configured.</p>
                          </div>
                        )}
                      </div>
                    </TabPane>
                  </TabContent>
                </>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>

      <ToastContainer />
    </>
  );
}
